/**
 * The PLAY drag-ghost's callout (`projects/off-turn-planning.md` 2.4, 3.2): a DOM box over the
 * canvas, above the ghost. A planning drag heads it with whose turn it is (`… — planning only`);
 * every PLAY drag lists what committing the move would provoke (`forecastMove`). Content comes from
 * `tokenDragPlan.ts`'s `calloutContent`; `CombatStage` renders this only when that is non-null.
 *
 * Position is written straight to the element's style (`placeGhostCallout`) rather than through
 * React props: the ghost moves every `mousemove`, and re-rendering the stage per pixel for a label
 * would be the wrong trade. Content changes only per drag cell, which is when this re-renders.
 */
import { useLayoutEffect, type RefObject } from "react";
import type { ForecastRow } from "./tokenDragPlan";

/** Screen px (relative to `.combat-stage-wrap`) of the ghost's top edge, horizontally centred. */
export type CalloutAnchor = { x: number; y: number };

export function placeGhostCallout(el: HTMLElement | null, anchor: CalloutAnchor | null): void {
  if (!el || !anchor) return;
  el.style.left = `${anchor.x}px`;
  el.style.top = `${anchor.y}px`;
}

export function GhostDragCallout({
  heading,
  rows,
  getAnchor,
  elRef,
}: {
  heading: string | null;
  rows: ForecastRow[];
  /** Read on every render, so a freshly mounted callout lands on the ghost before it paints. */
  getAnchor: () => CalloutAnchor | null;
  /** The stage writes position through this between renders (`placeGhostCallout`). */
  elRef: RefObject<HTMLDivElement | null>;
}) {
  useLayoutEffect(() => {
    placeGhostCallout(elRef.current, getAnchor());
  });
  return (
    <div
      ref={elRef}
      className="combat-ghost-callout"
      data-testid="ghost-drag-callout"
      role="status"
      aria-label="move forecast"
    >
      {heading !== null && (
        <div className="combat-ghost-callout-heading" data-testid="ghost-drag-callout-heading">
          {heading}
        </div>
      )}
      {rows.length > 0 && (
        <ul className="combat-ghost-callout-rows">
          {rows.map((r) => (
            <li key={r.key} className={`combat-ghost-callout-row is-${r.kind}`} data-testid="ghost-drag-callout-row" data-kind={r.kind}>
              {r.text}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
